import { FC, memo, useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { EdnaWrapper } from '@providers/index';

// TipTap
import { EditorContent } from '@tiptap/react';

// Internal
import { TextEditorProps, ToolbarOptionType } from './types';
import Toolbar from './Toolbar';
import InlinePopover from './InlinePopover';

const EditorStyles = createGlobalStyle`
.high {
  border-bottom: 2px solid ${(props) => props.theme.colors.green500} !important;
}
.medium {
  border-bottom: 2px solid ${(props) => props.theme.colors.yellow500};
}
.low {
  border-bottom: 2px solid ${(props) => props.theme.colors.grey500};
}
`;

const DEFAULT_TOOLS: ToolbarOptionType[] = [
  'uppercase',
  'lowercase',
  'titlecase',
  'divider',
  'undo',
  'redo',
  'divider',
  'copy',
];

const EditorWrapper = styled.div`
  width: 100%;
  border: 1px solid ${(props) => props.theme.colors.grey100};
  border-radius: 4px;
`;
const StyledEditorContent = styled(EditorContent)<{ $disabled?: boolean }>`
  width: 100%;
  background-color: white;
  border-radius: 4px;
  padding: 10px;
  position: relative;
  box-sizing: border-box;
  line-height: 1.5;
  p {
    margin-top: 0;
    font-size: 14px !important;
  }
  .ProseMirror {
    min-height: 90px;
    max-height: 700px;
    overflow: auto;
    &:focus-visible {
      outline: none;
    }
  }
  // Styling for the placeholder
  .ProseMirror p.is-editor-empty:first-child::before {
    color: ${(props) => props.theme.colors.grey200};
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
  }

  ${(props) =>
    props.$disabled &&
    ` 
    opacity: 0.5;
  `}
`;

/**
 * This component provides a text editor with a toolbar.
 *
 * The component requires an `editor` instance to be passed into it.
 *
 * To create your `editor`, import and use the `useTextEditor` hook.
 *
 * To this hook you can pass in the following props:
 * - `placeholder`: The placeholder text to display when the editor is empty. By default it will show 'Start typing your content here'.
 * - `options`: This is where you will want to capture events like onUpdate to track changes, and `onCreate` to set an initial value, etc. See the `useTextEditor` hook for more details.
 * - `deps`: This is an array of dependencies that will be used to determine when to re-initialize the editor.
 *
 * An example usage would be like so:
 *
 * ```jsx
 * import { useTextEditor, TextEditor } from '@junglescout/edna';
 *
 * const MyComponent = () => {
 *  const { editor } = useTextEditor({
 *    initialValue: 'Hello World',
 *    placeholder: 'My custom placeholder',
 *    options: {
 *      onUpdate: ({ editor }) => {
 *        console.log(editor.getText()); // this could also be editor.getHTML()
 *      },
 *      onCreate: ({ editor }) => {
 *        editor.commands.setContent('My initial value');
 *      }
 *    },
 *  });
 *
 *  return <TextEditor editor={editor} />;
 * }
 *
 * ```
 */
const TextEditor: FC<TextEditorProps> & {
  InlinePopover: typeof InlinePopover;
} = ({
  className,
  disabled = false,
  editor,
  tools = DEFAULT_TOOLS,
  children,
}: TextEditorProps) => {
  useEffect(() => {
    editor?.setOptions({ editable: !disabled });
  }, [disabled, editor]);

  if (!editor) {
    return null;
  }

  return (
    <EdnaWrapper>
      <EditorStyles />
      <EditorWrapper>
        <Toolbar editor={editor} tools={tools} disabled={disabled} />
        <StyledEditorContent
          editor={editor}
          className={className}
          aria-label="Text Editor"
          $disabled={disabled}
        >
          {children}
        </StyledEditorContent>
      </EditorWrapper>
    </EdnaWrapper>
  );
};

TextEditor.InlinePopover = InlinePopover;

export { TextEditor }; // Used just for storybook in order to view comments as documentation. Doesn't work with memo.
export default memo(TextEditor);
